import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";

// ID-080: collection-aware Add Variation — parent collection fixed, specs + pricing inherited, SKU proposed by the server
export const CollectionAddVariation = ({ c, open, onOpenChange, onCreated }) => {
  const [colors, setColors] = useState([]);
  const [picked, setPicked] = useState([]);
  const [ref, setRef] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!open) return;
    setPicked([]); setRef(""); setFile(null); setPreview(null); setErr("");
    api.get("/products").then(({ data }) => setColors(data.filter((p) => p.family_group === "ZELLIGE_TILE" && p.color_code && p.active && !p.archived)
      .map((p) => ({ code: p.color_code, name: p.moroccan_name || p.name })).sort((a, b) => a.code.localeCompare(b.code)))).catch(() => setColors([]));
  }, [open]);
  const lo = c.min_colors || 1, hi = c.max_colors || 8;
  const valid = picked.length >= lo && picked.length <= hi;
  useEffect(() => {
    if (!open || !valid) { setPreview(null); setErr(""); return; }
    const t = setTimeout(() => api.post(`/admin/mosaic-collections/${c.code}/variations`, { colors: picked, design_ref: ref || null, dry_run: true })
      .then(({ data }) => { setPreview(data); setErr(""); }).catch((e) => { setPreview(null); setErr(formatApiErrorDetail(e.response?.data?.detail)); }), 300);
    return () => clearTimeout(t);
  }, [picked, ref, open, valid, c.code]);
  const options = useMemo(() => colors.filter((x) => !picked.includes(x.code) && `${x.code} ${x.name}`.toLowerCase().includes(q.toLowerCase())), [colors, picked, q]);
  const name = (code) => colors.find((x) => x.code === code)?.name || "";
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/mosaic-collections/${c.code}/variations`, { colors: picked, design_ref: ref || null });
      if (file) { const fd = new FormData(); fd.append("file", file); await api.post(`/admin/variants/${data.variant_id}/images`, fd).catch(() => toast.error("Variation created; photo upload failed — add it from the table")); }
      toast.success(`${data.sku} added to ${c.name} — inactive until you activate it`); onOpenChange(false); onCreated?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-lg" data-testid="collection-add-variation">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">Add variation · {c.name}</DialogTitle>
          <DialogDescription className="text-xs text-warmgrey">Inherits {c.name}: tile {c.tile_size_in || "not set"}{c.mounting === "Mesh-mounted" ? ` · mesh sheet ${c.mesh_sheet_in || "not set"}` : ""} · {c.mounting} · {c.sqft_per_box} sq ft/box · collection dealer prices. Select {lo === hi ? lo : `${lo}–${hi}`} colors — no percentages needed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2" data-testid="cav-picked">
          {picked.map((code) => (
            <div key={code} className="flex items-center gap-2 rounded-md border border-border bg-sand/40 px-3 py-1.5" data-testid={`cav-picked-${code}`}>
              <span className="font-mono text-xs text-terracotta">{code}</span><span className="flex-1 text-sm text-charcoal">{name(code)}</span>
              <button type="button" onClick={() => setPicked((p) => p.filter((x) => x !== code))} aria-label="Remove color" data-testid={`cav-remove-${code}`}><X className="h-4 w-4 text-warmgrey" /></button>
            </div>
          ))}
          <p className="text-xs text-warmgrey" data-testid="cav-count">{picked.length} color{picked.length === 1 ? "" : "s"} selected</p>
        </div>
        {picked.length < hi && (
          <div>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a color by code or name" className="h-9 bg-white" data-testid="cav-search" />
            <div className="mt-2 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto" data-testid="cav-options">
              {options.map((x) => <button key={x.code} type="button" onClick={() => setPicked((p) => [...p, x.code])} data-testid={`cav-option-${x.code}`}
                className="rounded-full border border-border bg-white px-2.5 py-1 text-xs text-charcoal hover:border-terracotta"><span className="font-mono text-terracotta">{x.code}</span> {x.name}</button>)}
            </div>
          </div>
        )}
        <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Design / pattern reference (optional)" className="h-9 bg-white" data-testid="cav-design-ref" />
        <label className="text-xs text-warmgrey">Photo (optional)<input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 block text-xs" data-testid="cav-image" /></label>
        {preview && <p className="rounded-md border border-border bg-sand/30 p-2 text-sm text-charcoal" data-testid="cav-proposed-sku">Proposed SKU <span className="font-mono font-semibold">{preview.sku}</span> · starts inactive</p>}
        {err && <p className="text-xs text-status-sold" data-testid="cav-error">{err}</p>}
        <Button disabled={busy || !preview} onClick={submit} className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover" data-testid="cav-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create variation"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
